<template>
  <div class="tracking-advertiser-view">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onProgramSaved" />
    </router-view>

    <layout-logged-frame
      :back-url="backUrl"
      :title="activeAdvertiser.name"
      @beforeGoBack="onBackUrlClick"
    >
      <template v-slot:header-actions>
        <header-actions :actions="headerActions" />
      </template>

      <template v-slot:content>
        <v-card
          class="pa-8 elevation-0"
          :loading="isLoading"
        >
          <panels-tracking-advertiser :advertiser="activeAdvertiser" />

          <div class="mb-6 mt-4">
            <v-textarea
              v-model="noteEdit"
              :label="$t('note')"
              auto-grow
              variant="outlined"
              density="compact"
              rows="1"
              :hide-details="true"
              class="mb-3"
              @keydown.shift.enter.prevent="isNoteChanged && saveNote()"
            />
            <div v-if="isNoteChanged" class="d-flex gap-8 justify-end">
              <v-btn variant="text" size="small" @click="cancelEditNote">{{ $t('cancel') }}</v-btn>
              <v-btn color="primary" size="small" variant="outlined" :loading="isNoteLoading" @click="saveNote">{{ $t('save') }}</v-btn>
            </div>
          </div>

          <layout-section-header
            class="mt-4"
            :title="$t('domains')"
            :has-trigger="false"
          />

          <!-- 40px for delete button, 24px for margin -->
          <v-card
            border
            class="advertiser__subdomains mt-4 mb-8 border rounded-lg pa-4 pt-3 pb-3 elevation-0 d-flex justify-space-between align-center"
          >
            <div class="d-flex align-center" style="min-width: 0;">
              <v-icon class="mr-2">public</v-icon>
              <span class="font-weight-bold">{{ $t('auto_match_subdomains') }}</span>
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-icon
                    size="small"
                    v-bind="props"
                    class="material-icons-outlined ml-2"
                  >
                    info
                  </v-icon>
                </template>
                <span
                  class="d-block"
                  style="width: 200px;"
                >{{ $t('auto_match_subdomains_info') }}</span>
              </v-tooltip>
            </div>
            <v-switch
              v-model="isIncludeSubdomains"
              @update:modelValue="onSubdomainSwitchChange"
              :disabled="isLoading"
              class="mt-0 flex-0-0"
              inset
              density="compact"
              :hide-details="true"
            />
          </v-card>

          <panels-tracking-domains />
        </v-card>

        <table-tracking
          ref="programsTable"
          :advertiser="activeAdvertiser"
          :on-add="addAction?.onClick"
          :add-label="addAction?.title"
        />
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import backspaceToGoBack from '@/mixins/backspaceToGoBack';

  export default {
    mixins: [backspaceToGoBack],
    data() {
      return {
        isLoading: false,
        isIncludeSubdomains: null,
        isNoteLoading: false,
        noteEdit: '',
      };
    },
    computed: {
      addAction() {
        return this.headerActions.find(({ key, isDisabled }) => key === 'add' && !isDisabled);
      },
      headerActions() {
        return [
          {
            key: 'add',
            title: this.$t('new_program'),
            onClick: this.openAddProgramModal,
          },
        ];
      },
      // A plain path string, because layout-logged-frame's back-url prop only
      // accepts one — router.resolve builds it while keeping the list's query.
      backUrl() {
        return this.$router.resolve({ name: 'tracking-management', query: this.$route.query }).href;
      },
      activeAdvertiser() {
        return this.$store.getters['tracking/activeAdvertiser'];
      },
      isNoteChanged() {
        return this.noteEdit !== (this.activeAdvertiser.note || '');
      },
    },
    created() {
      // the advertiser listing returns a trimmed resource (no include_subdomains, no note),
      // so always fetch the full advertiser even when the active item already matches the route id
      this.getAdvertiser(this.$route.params.id);
    },
    watch: {
      activeAdvertiser: {
        immediate: true,
        deep: true,
        handler(newVal) {
          if (newVal) {
            this.isIncludeSubdomains = newVal.include_subdomains;
            if (newVal.note !== undefined) {
              this.noteEdit = newVal.note || '';
            }
          }
        }
      }
    },
    methods: {
      onProgramSaved() {
        this.$refs.programsTable.getPrograms();
      },
      onSubdomainSwitchChange(val) {
        // 'val' is the new boolean value
        // Only triggers on user interaction, so no "oldVal !== null" checks needed
        this.$store.dispatch('tracking/updateAdvertiser', {
          id: this.activeAdvertiser.id,
          include_subdomains: val,
        });
      },
      onBackUrlClick() {
        this.$store.dispatch('tracking/clearDomains');
      },
      getAdvertiser(id) {
        this.isLoading = true;
        this.$store.dispatch('tracking/getAdvertiser', id)
          .then((advertiser) => {
            this.isIncludeSubdomains = advertiser.include_subdomains;
          })
          .catch((err) => {
            return err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
      cancelEditNote() {
        this.noteEdit = this.activeAdvertiser.note || '';
      },
      saveNote() {
        this.isNoteLoading = true;
        this.$store.dispatch('tracking/updateAdvertiser', {
          id: this.activeAdvertiser.id,
          note: this.noteEdit || null,
        })
        .catch((err) => err)
        .finally(() => {
          this.isNoteLoading = false;
        });
      },
      openAddProgramModal() {
        this.$router.push({ name: 'tracking-management-advertiser-program-new', query: this.$route.query });
      },
    },
    metaInfo() {
      return {
        title: this.activeAdvertiser.name,
      };
    }
  };
</script>

<style lang="scss" scoped>
@use '../../assets/styles/theme' as *;

// Lines up with the domain rows' delete buttons, which leaves a phone too
// little room for the label and the switch.
.advertiser__subdomains {
  max-width: calc(100% - 40px - 24px);

  @include phone {
    max-width: none;
  }
}
</style>
